import { useCallback, useEffect, useRef, useState } from 'react';
import { listOperationalIssues } from '../../../services/operationalIssues';
import { describeHttpError } from '../../../utils/apiError';
import { APP_EVENTS, useAppEvent } from '../../../utils/appEvents';

export const ISSUES_PAGE_SIZE = 50;

/**
 * ТЗ №2 §7: URL сохраняет status/factory_id/camera_id/offset/issue_id.
 * ТЗ v4 §30: archive_state — отдельная ось, поэтому и отдельный параметр;
 * lifecycle status в нём не участвует.
 */
export const ISSUES_URL_SCHEMA = {
  archiveState: { param: 'archive_state', default: 'active' },
  status: { param: 'status', default: '' },
  factoryId: { param: 'factory_id', default: '' },
  cameraId: { param: 'camera_id', default: '' },
  offset: { param: 'offset', default: 0, type: 'number' },
  issueId: { param: 'issue_id', default: '' },
};

const isCanceled = (error) => error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';

const classifyError = (error) => {
  const status = error?.response?.status;
  if (status === 422) return { kind: 'validation', message: describeHttpError(error, 'Некорректный фильтр') };
  if (status) return { kind: 'server', message: describeHttpError(error, 'Не удалось загрузить системные проблемы') };
  return { kind: 'network', message: 'Нет связи с сервером' };
};

/**
 * ТЗ №2 §11: stale request игнорируется (sequence + abort); при refresh
 * список не обнуляется; 5xx/network ≠ empty. 200+total=0 — нормальный empty.
 */
export const useOperationalIssues = ({ archiveState, status, factoryId, cameraId, offset }) => {
  const [data, setData] = useState(null);
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const seqRef = useRef(0);

  useEffect(() => {
    const seq = ++seqRef.current;
    const controller = new AbortController();
    setState('loading');
    setError(null);
    listOperationalIssues({
      archive_state: archiveState || 'active',
      ...(status ? { status } : {}),
      ...(factoryId ? { factory_id: Number(factoryId) } : {}),
      ...(cameraId ? { camera_id: Number(cameraId) } : {}),
      limit: ISSUES_PAGE_SIZE,
      offset: Number(offset) || 0,
    }, { signal: controller.signal })
      .then((response) => {
        if (seq !== seqRef.current) return;
        setData(response);
        setState('ready');
      })
      .catch((requestError) => {
        if (seq !== seqRef.current || isCanceled(requestError)) return;
        setError(classifyError(requestError));
        setState('error');
      });
    return () => controller.abort();
  }, [archiveState, status, factoryId, cameraId, offset, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);
  // Дополнение к BM-FE-TZ-002C, п. 4: backend фильтрует проблемы по текущей
  // категории правила — после смены категории список и счётчики перечитываются.
  useAppEvent(APP_EVENTS.RULE_CATEGORY_CHANGED, reload);

  return { data, status: state, error, reload };
};

export default useOperationalIssues;
